/**
 * Fill in a portal page's stored images: every `img[data-storage-path]` under
 * `root`, signed in ONE request rather than one per image.
 *
 * An image that isn't signed — the request failed, or the link doesn't cover
 * that file — is removed rather than left as a broken icon. A cover photo
 * takes its frame with it, so no empty cream box sits beside the title.
 */

export type Signer = (paths: string[]) => Promise<Record<string, string>>;

export async function hydratePortalImages(
  root: ParentNode, sign: Signer,
): Promise<{ signed: number; hidden: number }> {
  const imgs = [...root.querySelectorAll<HTMLImageElement>('img[data-storage-path]')];
  const paths = [...new Set(imgs.map((i) => i.getAttribute('data-storage-path') ?? '').filter(Boolean))];
  if (paths.length === 0) return { signed: 0, hidden: 0 };

  let urls: Record<string, string> = {};
  try {
    urls = await sign(paths);
  } catch {
    urls = {};
  }

  let signed = 0;
  let hidden = 0;
  for (const img of imgs) {
    const url = urls[img.getAttribute('data-storage-path') ?? ''];
    if (url) {
      img.src = url;
      signed++;
    } else {
      hideImage(img);
      hidden++;
    }
  }
  return { signed, hidden };
}

function hideImage(img: HTMLImageElement): void {
  const frame = img.closest<HTMLElement>('.client-hero-image, .room-card-thumb');
  if (frame) {
    frame.closest('.client-hero')?.classList.remove('has-image');
    frame.remove();
  } else {
    img.hidden = true;
  }
}
